import { useState, useCallback, ReactNode } from 'react'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'

interface ConfirmOptions {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'warning' | 'default'
}

interface UseConfirmReturn {
  confirm: (options: ConfirmOptions) => Promise<boolean>
  ConfirmDialog: () => ReactNode
}

export function useConfirm(): UseConfirmReturn {
  const [state, setState] = useState<{
    isOpen: boolean
    options: ConfirmOptions
    resolve: ((value: boolean) => void) | null
  }>({
    isOpen: false,
    options: { title: '', message: '' },
    resolve: null,
  })

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({ isOpen: true, options, resolve })
    })
  }, [])

  const handleConfirm = useCallback(() => {
    state.resolve?.(true)
    setState((s) => ({ ...s, isOpen: false, resolve: null }))
  }, [state.resolve])

  const handleCancel = useCallback(() => {
    state.resolve?.(false)
    setState((s) => ({ ...s, isOpen: false, resolve: null }))
  }, [state.resolve])

  const ConfirmDialog = useCallback(
    () =>
      state.isOpen ? (
        <ConfirmationModal
          isOpen={state.isOpen}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          title={state.options.title}
          message={state.options.message}
          confirmLabel={state.options.confirmLabel}
          cancelLabel={state.options.cancelLabel}
          variant={state.options.variant}
        />
      ) : null,
    [state.isOpen, state.options, handleConfirm, handleCancel]
  )

  return { confirm, ConfirmDialog }
}
